import { JSONParser } from '@streamparser/json';
import { AxiosRequestConfig } from 'axios';
import debounce from 'lodash/debounce';
import { useCurrentEnterprise } from '../contexts/useCurrentEnterprise';
import { getBffUrl } from './EnvironmentUtils';
import { LOGIN_URL } from './Urls.constant';

export interface CancellablePromise<T> extends Promise<T> {
    cancel: () => void;
}

export interface FetchError<T = any> extends Error {
    response?: {
        status: number;
        statusText: string;
        data: T;
        headers: Record<string, string>;
    };
}

const REFRESH_TOKEN_DEBOUNCE = 2000;
const REDIRECT_TO_LOGIN_DEBOUNCE = 2000;

const redirectToLoginDebounce = debounce(() => {
    if (typeof window !== 'undefined') {
        window.location.replace(LOGIN_URL);
    }
}, REDIRECT_TO_LOGIN_DEBOUNCE);

const refreshTokenDebounce = debounce(async () => {
    if (typeof window === 'undefined') {
        return;
    }

    const baseURL = getBffUrl(window.location.host);
    const url = `${baseURL}/Users/RefreshToken`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-csrf': '1',
            },
            credentials: 'include',
        });
        if (response.status === 401) redirectToLoginDebounce();
        const data = await response.text();
        if (data) redirectToLoginDebounce();
    } catch {
        redirectToLoginDebounce();
    }
}, REFRESH_TOKEN_DEBOUNCE);

export const useCustomInstance = <T>(): ((config: AxiosRequestConfig) => CancellablePromise<T>) => {
    const { enterprise } = useCurrentEnterprise();
    const baseURL = typeof window !== 'undefined' ? getBffUrl(window.location.host) : getBffUrl('');

    return (config: AxiosRequestConfig) => {
        if (refreshTokenDebounce) refreshTokenDebounce.cancel();
        refreshTokenDebounce();

        const controller = new AbortController();

        let url = `${baseURL}${config.url || ''}`;
        url = appendQueryParams(url, config.params);

        const headers = createFetchHeaders(enterprise?.enterpriseKey, config);

        const fetchConfig = createFetchConfig(config.method, headers, controller.signal, config.data);

        const cancellableRequest = (async (): Promise<T> => {
            try {
                const response = await fetch(url, fetchConfig);

                if (!response.ok) {
                    const errorData = await parseErrorResponse(response);
                    const error: FetchError = new Error(`HTTP ${response.status}: ${response.statusText}`) as FetchError;
                    error.response = {
                        status: response.status,
                        statusText: response.statusText,
                        data: errorData,
                        headers: Object.fromEntries(response.headers.entries()),
                    };

                    if (response.status === 403 || response.status === 401) {
                        const isPillar2JurisdictionCall = (config.url || '').startsWith('/pillar2/');
                        const redirectUrl = response.headers.get('x-corptax-redirect-uri');
                        if (redirectUrl && typeof window !== 'undefined') {
                            window.location.href = redirectUrl;
                            return null as T;
                        } else if (!isPillar2JurisdictionCall) {
                            redirectToLoginDebounce();
                        }
                    }

                    throw error;
                }

                if (config.responseType === 'blob') {
                    return (await response.blob()) as unknown as T;
                }

                const contentType = response.headers.get('content-type') || '';
                if (contentType.includes('application/json')) {
                    return await parseJsonResponse<T>(response);
                }

                const text = await response.text();
                return text as unknown as T;
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    const abortError: FetchError = new Error('Request cancelled') as FetchError;
                    abortError.name = 'AbortError'; // Preserve the error name for proper error handling
                    throw abortError;
                }
                if (error && typeof error === 'object' && 'response' in error) {
                    throw error;
                }
                const fetchError: FetchError = error as FetchError;
                throw fetchError;
            }
        })() as CancellablePromise<T>;

        cancellableRequest.cancel = () => {
            controller.abort();
        };

        return cancellableRequest;
    };
};

const appendQueryParams = (url: string, params?: Record<string, any>): string => {
    if (!params) {
        return url;
    }

    const urlSearchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (Array.isArray(v)) {
            v.forEach((item, index) => {
                urlSearchParams.append(`${k}[${index}]`, String(item));
            });
        } else if (v !== null && v !== undefined) {
            urlSearchParams.append(k, String(v));
        }
    });

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${urlSearchParams.toString()}`;
};

const isValidHTMLTag = (str: string) => {
    try {
        const regexAllHtmlTags = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]*))$/;
        const regex = new RegExp(regexAllHtmlTags);
        return regex.test(str);
    } catch (e) {
        console.log(e);
    }
};

const getAcceptHeaderValue = (config: AxiosRequestConfig): string => {
    let acceptHeaderValue = 'application/json, text/plain';

    if (config.responseType === 'blob') {
        acceptHeaderValue += ', application/octet-stream';
    }

    return acceptHeaderValue;
};

const createFetchHeaders = (enterpriseKey: string | number | undefined, config: AxiosRequestConfig): Record<string, string> => {
    return {
        'x-csrf': '1',
        'x-enterprise-key': enterpriseKey?.toString() || '',
        Accept: getAcceptHeaderValue(config),
        ...(config.headers as Record<string, string>),
    };
};

const parseErrorResponse = async (response: Response): Promise<any> => {
    const contentType = response.headers.get('content-type') || '';
    try {
        if (contentType.includes('application/json')) {
            return await response.json();
        }
        return await response.text();
    } catch {
        return null;
    }
};

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
    const reader = response.body?.getReader();
    if (!reader) {
        const text = await response.text();

        const isValidHTML = isValidHTMLTag(text) ?? false;
        if (isValidHTML) {
            return redirectToLoginDebounce();
        }

        return JSON.parse(text) as T;
    }

    const parser = new JSONParser();
    let result: T | null = null;

    parser.onValue = (elementInfo: any) => {
        if (elementInfo?.value !== undefined) {
            result = elementInfo.value as T;
        }
    };

    const decoder = new TextDecoder();

    const pump = async (): Promise<void> => {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });
            parser.write(chunk);
        }
    };

    await pump();
    return result as T;
};

const createFetchConfig = (method: string | undefined, headers: Record<string, string>, signal: AbortSignal, data?: any): RequestInit => {
    const config: RequestInit = {
        method: (method || 'get').toUpperCase(),
        headers,
        credentials: 'include',
        signal,
    };

    if (data && method && ['post', 'put', 'patch', 'delete'].includes(method.toLowerCase())) {
        if (typeof data === 'string') {
            config.body = data;
        } else if (data instanceof FormData) {
            config.body = data;
            delete headers['Content-Type'];
        } else if (data instanceof Blob) {
            config.body = data;
        } else {
            config.body = JSON.stringify(data);
            headers['Content-Type'] = headers['Content-Type'] || 'application/json';
        }
    }

    return config;
};

export default useCustomInstance;

export type ErrorType<Error> = FetchError<Error>;
